import {Quiz} from "../models/Quiz";

const CACHE_NAME = "geared-v1";

/**
 * Map of original sound URL -> playable URL. Prefetched sounds resolve to
 * blob object URLs (playable offline); anything else falls back to the
 * original URL, which streams over the network.
 */
export type SoundMap = Map<string, string>;

async function fetchViaCache(url: string): Promise<Blob> {
    if (!("caches" in window)) {
        const response = await fetch(url);
        if (!response.ok) {
            throw new Error(`Failed to fetch sound: ${response.status}`);
        }
        return response.blob();
    }

    const cache = await caches.open(CACHE_NAME);
    let response = await cache.match(url);
    if (!response) {
        response = await fetch(url);
        if (!response.ok) {
            throw new Error(`Failed to fetch sound: ${response.status}`);
        }
        await cache.put(url, response.clone());
    }
    return response.blob();
}

/**
 * Downloads all of the day's sounds (full + 1s clips) in parallel into blob
 * object URLs, persisting them in the Cache API so reloads replay from disk.
 * Individual failures are tolerated — those sounds stream directly instead.
 */
export async function prefetchSounds(quiz: Quiz): Promise<SoundMap> {
    const urls = new Set<string>();
    quiz.questions.forEach((q) => {
        urls.add(q.soundUrl);
        if (q.soundUrl1s) {
            urls.add(q.soundUrl1s);
        }
    });

    const sounds: SoundMap = new Map();
    await Promise.all(Array.from(urls).map(async (url) => {
        try {
            const blob = await fetchViaCache(url);
            sounds.set(url, URL.createObjectURL(blob));
        } catch {
            sounds.set(url, url);
        }
    }));
    return sounds;
}

/** Resolves a sound URL to its prefetched blob URL when available. */
export function resolveSound(sounds: SoundMap, url: string): string {
    return sounds.get(url) ?? url;
}
